import classNames from 'classnames'
import * as React from 'react'

import { RedGreenText } from '../../components/RedGreenText'
import { TableAlign } from '../../components/Table'
import { FinancialsSubRowDataType } from '../../components/financials-table/types'
import { shortFormatNumber } from '../../util'
import { EarningValueProperty, EarningsColumnsType, EarningsDataEnum } from './types'

function renderSubColumnValue(data: FinancialsSubRowDataType, id: string) {
  const mainEarning = data.values.find((mainEarning) => mainEarning.year === id)
  const mainEarningValue = mainEarning?.value ?? null
  return (
    <RedGreenText value={mainEarning?.isEstimate ? null : mainEarningValue} rightContent={null}>
      {shortFormatNumber(mainEarningValue, { defaultValue: '—' })}
    </RedGreenText>
  )
}

function renderSubColumnValuePct(data: FinancialsSubRowDataType, id: string) {
  const mainEarningPct = data.values.find((mainEarningPct) => mainEarningPct.year === id)
  const mainEarningPctValue = mainEarningPct?.value ?? null
  return (
    <RedGreenText
      value={mainEarningPct?.isEstimate ? null : mainEarningPctValue}
      rightContent={Number.isFinite(mainEarningPctValue) ? '%' : null}
    >
      {mainEarningPctValue?.toFixed(2) ?? '—'}
    </RedGreenText>
  )
}

const getColumns = (yearKeys: string[], isCompact: boolean) => {
  const commonClassName = classNames('px-4 last:pr-11', {
    'w-22 last:w-29.5': !isCompact,
    'w-17 last:w-24.5': isCompact,
  })

  const columns: EarningsColumnsType['columns'] = yearKeys.map((year) => ({
    id: year,
    name: year,
    align: TableAlign.Right,
    className: commonClassName,
    renderColumn: (data) => {
      const annualDividend = data.values.find((annualDividend) => annualDividend.year === year)
      const annualDividendValue = annualDividend?.value ?? null
      const annualDividendValuePercent = annualDividend?.percentageValue ?? null
      if (!Number.isFinite(annualDividendValue)) return <span className="text-muted-2">—</span>
      if (data.id === EarningValueProperty.Surprise) {
        return (
          <RedGreenText value={annualDividendValue} rightContent={null}>
            {shortFormatNumber(annualDividendValue, { defaultValue: '—' })}
            {Number.isFinite(annualDividendValuePercent) && (
              <>
                <br />
                {shortFormatNumber(annualDividendValuePercent, { fractions: 2 })}%
              </>
            )}
          </RedGreenText>
        )
      }
      const value = shortFormatNumber(annualDividendValue, { fractions: 2 })
      return <span className={classNames({ 'text-muted-2': annualDividend?.isEstimate })}>{value}</span>
    },
  }))

  const subColumns: EarningsColumnsType['subColumns'] = {
    [EarningsDataEnum.analysts]: yearKeys.map((year) => ({
      id: year,
      name: year,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: (data) => {
        const annualDividend = data.values.find((annualDividend) => annualDividend.year === year)
        const annualDividendValue = annualDividend?.value ?? null
        return <span className="text-muted-2">{annualDividendValue?.toFixed(0) ?? '—'}</span>
      },
    })),
    [EarningsDataEnum.mainChange]: yearKeys.map((year) => ({
      id: year,
      name: year,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: renderSubColumnValue,
    })),
    [EarningsDataEnum.mainChangePct]: yearKeys.map((year) => ({
      id: year,
      name: year,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: renderSubColumnValuePct,
    })),
    [EarningsDataEnum.extraChangeYoYonQoQ]: yearKeys.map((year) => ({
      id: year,
      name: year,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: renderSubColumnValue,
    })),
    [EarningsDataEnum.extraChangePctYoYonQoQ]: yearKeys.map((year) => ({
      id: year,
      name: year,
      align: TableAlign.Right,
      className: commonClassName,
      renderColumn: renderSubColumnValuePct,
    })),
  }
  return { columns, subColumns }
}

export function useTableColumns(yearKeys: string[], isCompact: boolean) {
  return React.useMemo(() => getColumns(yearKeys, isCompact), [yearKeys, isCompact])
}
